import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Preferences card — single Phase-7.5 toggle to ignore friendly matches
 * in every FBM-related read path. Persisted via PATCH /api/auth/me/settings.
 */
export default function PreferencesSection({ testIdPrefix = "preferences" }) {
  const { user, refreshMe } = useAuth();
  const initial = user?.ignore_friendlies ?? true;
  const [value, setValue] = useState(Boolean(initial));
  const [saving, setSaving] = useState(false);

  const onToggle = async (next) => {
    // Optimistic UI; rollback on error
    const previous = value;
    setValue(next);
    setSaving(true);
    try {
      await api.patch("/auth/me/settings", { ignore_friendlies: next });
      await refreshMe();
      toast.success(
        next
          ? "Friendlies will be ignored in FBM calculations"
          : "Friendlies will be included in FBM calculations"
      );
    } catch (err) {
      setValue(previous);
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-card p-6 space-y-4"
      data-testid={`${testIdPrefix}-section`}
    >
      <div>
        <h2 className="font-display uppercase tracking-tight text-xl">Preferences</h2>
        <p className="text-xs text-muted-foreground mt-1">
          Control how FBM stats are calculated across your scouting view.
        </p>
      </div>

      <div className="flex items-start justify-between gap-6 pt-2">
        <div className="space-y-1.5 max-w-sm">
          <Label
            htmlFor={`${testIdPrefix}-ignore-friendlies`}
            className="text-sm font-medium"
          >
            Ignore friendlies
          </Label>
          <p className="text-xs text-muted-foreground leading-relaxed">
            When enabled, friendly matches (any competition whose name contains
            "friendly" or "friendlies") are excluded from FBM probabilities,
            distribution curves, the season timeline and the match list. Turn
            off to include them in all calculations.
          </p>
        </div>
        <Switch
          id={`${testIdPrefix}-ignore-friendlies`}
          checked={value}
          onCheckedChange={onToggle}
          disabled={saving}
          data-testid={`${testIdPrefix}-ignore-friendlies-toggle`}
        />
      </div>
    </div>
  );
}
